import type { FC, PropsWithChildren } from 'react';

const defaultIncrementStep = 1;

type Props = PropsWithChildren<{
  onBlur?: (value?: number) => void;
  onChange?: (value?: number) => void;
  value?: Nullable<number>;
  incrementStep?: number;
  isDisabled?: boolean;
  isLoading?: boolean;
  className?: string;
}>;

export type WithIncrementersOptions = { triggerType?: 'change' | 'blur' } & (
  | { layout?: 'around' }
  | { layout: 'end'; endClassName?: string }
);

export const withIncrementers = <T extends Props>(Component: FC<T>, opts: WithIncrementersOptions): FC<T> => {
  return (props: T) => {
    const changeHandler = opts.triggerType === 'change' ? props.onChange : props.onBlur;
    const layout = opts.layout ?? 'around';
    const onDecrement = () => {
      if (changeHandler) {
        if (props.value !== undefined && props.value !== null) {
          changeHandler(props.value - (props.incrementStep || defaultIncrementStep));
        } else {
          changeHandler(-(props.incrementStep || defaultIncrementStep));
        }
      }
    };

    const onIncrement = () => {
      if (changeHandler) {
        if (props.value !== undefined && props.value !== null) {
          changeHandler(props.value + (props.incrementStep || defaultIncrementStep));
        } else {
          changeHandler(props.incrementStep || defaultIncrementStep);
        }
      }
    };
    const btnClassName = layout === 'end' ? 'btn-icon text-primary' : 'btn-discreet-secondary';
    const btnStyle = layout === 'end' ? { width: 24, height: 24 } : undefined;
    const minusBtn = (
      <button
        className={`btn ${btnClassName}`}
        data-e2e="decrement-button"
        disabled={props.isDisabled || props.isLoading}
        onClick={onDecrement}
        style={btnStyle}
      >
        -
      </button>
    );
    const plusBtn = (
      <button
        className={`btn ${btnClassName}`}
        data-e2e="increment-button"
        disabled={props.isDisabled || props.isLoading}
        onClick={onIncrement}
        style={btnStyle}
      >
        +
      </button>
    );
    return (
      <div className="d-inline-flex align-items-center w-100">
        {layout === 'around' && minusBtn}
        <Component {...props} incrementStep={props.incrementStep || defaultIncrementStep} />
        {layout === 'around' && plusBtn}
        {opts.layout === 'end' && (
          <div className={`d-flex flex-column ${opts.endClassName}`}>
            {plusBtn}
            {minusBtn}
          </div>
        )}
      </div>
    );
  };
};
